import { createElement, memo } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import IconResolver from './IconResolver';
// import { useFontLoaded } from '../../../contexts/FontLoadContext';
import useIcons from '../../../hooks/useIcons';
import getColorFromVariant from '../../../utils/getColorFromVariant';

//* This component uses the Material Symbols library
// To use it, pass the icon name (ex : 'open_in_new') as argument to 'icon' prop
// ? Variants : primary, secondary, info, warning, critical, success
// ? Material Symbols library : https://fonts.google.com/icons?icon.style=Outlined

/**
 * Renders an icon with optional badge. This component supports both custom icons and Material Symbols.
 * It automatically selects custom icons defined in `useIcons` hook; if not found, it falls back to Material Symbols.
 * The icon's color can be themed based on predefined variant names. It also supports a filled version for Material Symbols
 * and displays badges for additional visual cues about the icon's context or state.
 *
 * @param {object} props - Component props.
 * @param {string} [props.icon=''] - The icon name, which should match either a custom icon or a Material Symbol name.
 * @param {string} [props.variant=''] - The color theme variant name, used to determine the icon color. Supported variants are: 'primary', 'secondary', 'info', 'warning', 'critical', 'success'.
 * @param {boolean} [props.filled=false] - Determines if the Material Symbol should be filled. Applies only to Material Symbols, not custom icons.
 * @param {object} other - Other props to spread onto either the custom icon component or the `IconResolver` for Material Symbols.
 * @returns {JSX.Element} An icon.
 */
const MIcon = (props) => {
  const { icon = '', variant = '', fontSize = 24, filled = false, ...other } = props;

  // const { fontLoaded } = useFontLoaded(); // ! undefined lorsqu'on valide une PDS en tant que manager
  const fontLoaded = true; // ! quick and dirty fix to prevent this error
  // ! Cannot destructure property 'fontLoaded' of '(0 , _contexts_FontLoadContext__WEBPACK_IMPORTED_MODULE_2__.useFontLoaded)(...)' as it is undefined.

  const theme = useTheme();
  const color = variant ? getColorFromVariant(theme, variant) : null;
  const customIcons = useIcons();
  const loaded = `${fontLoaded ? ' loaded' : ''}`;
  const iconType = filled ? ' filled' : '';

  // className allow to use the fontVariationSettings FILL: 1 (cf. globalStyles)
  const className = `icon material-symbols-outlined ${loaded} ${iconType}`;

  // If a custom icon doesn't exists in assets/icons, we use the Material Symbol icon.
  const customIcon = customIcons.find((obj) => Object.prototype.hasOwnProperty.call(obj, icon));
  const CustomIcon = () =>
    customIcon
      ? createElement(customIcon[icon], {
          className,
          color,
          fontSize,
          filled,
          height: fontSize,
          width: fontSize,
          ...other
        })
      : null;

  return customIcon ? (
    <CustomIcon />
  ) : (
    <IconResolver className={className} icon={icon} fontSize={fontSize} color={color} filled={filled} {...other} />
  );
};

MIcon.propTypes = {
  icon: PropTypes.string.isRequired,
  filled: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variant: PropTypes.string
};

export default memo(MIcon);
